/**
 * How one field's value reads, and how a change to it reads.
 *
 * Three renderers rather than one because a value appears in three shapes on
 * this screen: alone, as a before-and-after pair inside a sentence, and as the
 * two panels under an expanded change.
 *
 * @module components/portfolio/comparison/change-values
 */

import React from "react";

import { humanizeIdentifier } from "@/components/shared/diff-labels";
import { Disclosure, Eyebrow, KeyValue, Panel } from "@/components/ui";

/** What a value looks like when it is small enough to say inline. */
export function displayPrimitive(value: unknown): string {
  if (value === undefined) {
    return "Not present";
  }
  if (value === null || value === "") {
    return "Not set";
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "Structured content";
}

export interface PrimitiveTransitionProps {
  before: unknown;
  after: unknown;
}

/**
 * @example
 * ```tsx
 * <PrimitiveTransition before="Draft" after="Approved" />
 * ```
 */
export function PrimitiveTransition({
  before,
  after,
}: PrimitiveTransitionProps): React.JSX.Element {
  return (
    <>
      {displayPrimitive(before)} <span aria-hidden>→</span>
      <span className="sr-only"> changed to </span> {displayPrimitive(after)}
    </>
  );
}

export interface ReadableValueProps {
  value: unknown;
}

/**
 * A value of any shape, opened one level at a time.
 *
 * Recursive, so a `Disclosure` rather than `AccordionItem`: the nesting depth
 * is the data's, not the screen's, and every level is the same control.
 *
 * @example
 * ```tsx
 * <ReadableValue value={change.after} />
 * ```
 */
export function ReadableValue({ value }: ReadableValueProps): React.JSX.Element {
  if (Array.isArray(value)) {
    const occurrences = new Map<string, number>();
    const items = value.map((item) => {
      const serialised = JSON.stringify(item) ?? String(item);
      const occurrence = (occurrences.get(serialised) ?? 0) + 1;
      occurrences.set(serialised, occurrence);
      return { item, key: `${serialised}:${occurrence}` };
    });
    return (
      <Disclosure summary={`View ${value.length} ${value.length === 1 ? "item" : "items"}`}>
        <ul className="mt-2 space-y-2 border-l border-base-300 pl-4">
          {items.map(({ item, key }) => (
            <li key={key}>
              <ReadableValue value={item} />
            </li>
          ))}
        </ul>
      </Disclosure>
    );
  }
  if (typeof value === "object" && value !== null) {
    const entries = Object.entries(value);
    return (
      <Disclosure summary={`View ${entries.length} ${entries.length === 1 ? "field" : "fields"}`}>
        <KeyValue
          valueWeight="prose"
          layout="stacked"
          className="mt-2"
          entries={entries.map(([key, entry]) => ({
            label: humanizeIdentifier(key),
            value: <ReadableValue value={entry} />,
          }))}
        />
      </Disclosure>
    );
  }
  return <span>{displayPrimitive(value)}</span>;
}

export interface ChangeValuesProps {
  change: { before?: unknown; after?: unknown };
}

/**
 * @example
 * ```tsx
 * <ChangeValues change={{ before: 10, after: 15 }} />
 * ```
 */
export function ChangeValues({ change }: ChangeValuesProps): React.JSX.Element {
  return (
    <div className="mt-3 grid gap-3 text-md md:grid-cols-2">
      <Panel tone="inset" density="compact" className="min-w-0">
        <Eyebrow as="div" className="mb-1">
          Earlier
        </Eyebrow>
        <ReadableValue value={change.before} />
      </Panel>
      <Panel tone="inset" density="compact" className="min-w-0">
        <Eyebrow as="div" className="mb-1">
          Later
        </Eyebrow>
        <ReadableValue value={change.after} />
      </Panel>
    </div>
  );
}
